import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { Button, Typography } from '@mui/material';
import { onAutocompleteChangeInForm } from 'app/shared/components/form/Autocomplete';
import { AutocompleteCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import PropTypes from 'prop-types';

import { previewFormCoborrowersOptions, previewFormNonborrowersOptions } from 'constants/options';
import useForm from 'hooks/useForm';

import { closeDialogAction } from 'store/global/actions';

import { formsTestIds } from '../forms.testIds';

const PrismExportSignersModal = ({
  document,
  margins,
  pageNumbering,
  watermark = null,
  onSubmit,
}) => {
  const dispatch = useDispatch();

  const { form, isFormChanged, setInForm, validateField } = useForm({
    coborrowersNumber: previewFormCoborrowersOptions[0],
    nonborrowersNumber: previewFormNonborrowersOptions[0],
  });

  const coborrowersNumberInForm = form.coborrowersNumber.value;
  const nonborrowersNumberInForm = form.nonborrowersNumber.value;

  const onFormSubmit = useCallback(
    e => {
      e.preventDefault();

      onSubmit({
        document,
        margins,
        pageNumbering,
        watermark,
        coBorrowersCount: coborrowersNumberInForm,
        nonBorrowersCount: nonborrowersNumberInForm,
      });

      dispatch(closeDialogAction());
    },
    [
      dispatch,
      document,
      margins,
      pageNumbering,
      watermark,
      onSubmit,
      coborrowersNumberInForm,
      nonborrowersNumberInForm,
    ],
  );

  const onAutocompleteChange = useMemo(
    () => onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  return (
    <form
      noValidate
      className="flex flex-col justify-center p-24"
      data-ischanged={isFormChanged}
      onSubmit={onFormSubmit}
    >
      <AutocompleteCDS
        disableClearable
        className="my-8"
        dataTestId={formsTestIds.PRISM_EXPORT_SIGNERS_COBORROWERS_SELECT}
        label="Number of co-borrowers"
        name="coborrowersNumber"
        options={previewFormCoborrowersOptions}
        value={form.coborrowersNumber || ''}
        onChange={onAutocompleteChange}
      />

      <AutocompleteCDS
        disableClearable
        className="my-8"
        dataTestId={formsTestIds.PRISM_EXPORT_SIGNERS_NONBORROWERS_SELECT}
        label="Number of NBEs"
        name="nonborrowersNumber"
        options={previewFormNonborrowersOptions}
        value={form.nonborrowersNumber || ''}
        onChange={onAutocompleteChange}
      />

      <Typography className="italic" color="textSecondary" variant="regular12">
        The application includes Primary Borrower
        {coborrowersNumberInForm
          ? `${nonborrowersNumberInForm ? ',' : ' and'} ${coborrowersNumberInForm} Co-borrower${
              coborrowersNumberInForm > 1 ? 's' : ''
            }`
          : ``}
        {nonborrowersNumberInForm
          ? ` and ${nonborrowersNumberInForm} NBE${nonborrowersNumberInForm > 1 ? 's' : ''}`
          : ``}
        .
      </Typography>

      <Button
        className="whitespace-nowrap self-end"
        data-testid={formsTestIds.PRISM_EXPORT_SIGNERS_SUBMIT_BUTTON}
        type="submit"
        variant="contained"
      >
        Export
      </Button>
    </form>
  );
};

PrismExportSignersModal.propTypes = {
  document: PropTypes.object.isRequired,
  margins: PropTypes.object.isRequired,
  pageNumbering: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  watermark: PropTypes.object,
};

export default memo(PrismExportSignersModal);
